import { Box, Dialog, DialogActions, DialogContent, FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select, Stack } from "@mui/material";
import { MprcButton, MprcConfirmDialog, MprcDialogTitleClose, MprcRequestErrorReport, MprcRequestIndicator, MprcStringToHtml } from "components";
import { ButtonCancel, ButtonSave } from "components/buttons";
import { MprcDatePicker } from "components/MprcDatePicker";
import { AbsenceTypeSelectorResponse, AddAbsencePayload, UserAbsenceResponse } from "generated/CoreApi_v1";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { UserAutoComplete } from "views/foundation/user/components/UserAutoComplete";

////////////////////////////////////////////////////////////////////////////////

interface IPros {
    userId: string;
    name: string;
    onClose: (success: boolean) => void;
}

export interface IPostValidation {
    absenceTypeId: string[] | undefined;
    validFrom: string[] | undefined;
    validUntil: string[] | undefined;
    delegateUserId: string[] | undefined;
}

////////////////////////////////////////////////////////////////////////////
/// FUNCTIONS
////////////////////////////////////////////////////////////////////////////

///
function getEmptyDto(): UserAbsenceResponse {
    return {
        userAbsenceId: "",
        absenceTypeId: "",
        validFrom: null!,
        validUntil: null,
        delegateUser: [],
    };
}

///
function buildPostDto(userId: string, dto: UserAbsenceResponse): AddAbsencePayload {
    return {
        userId: userId,
        absenceTypeId: dto.absenceTypeId!,
        validFrom: dto.validFrom,
        validUntil: dto.validUntil,
        delegateUserId: dto.delegateUser.length > 0 ? dto.delegateUser[0].key : null
    };
}

/////////////////////////////////////////////////////////////////////////////////

export const UserAbsenceAssistantDialog: React.FC<IPros> = (props) => {
    const api = useCoreApi();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const { onClose, userId, name } = props;

    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [deactivating, setDeactivating] = useState<boolean>(false);
    const [dialog, setDialog] = useState<any>();
    const [viewData, setViewData] = useState<UserAbsenceResponse>(() => getEmptyDto());
    const absenceTypes = useRef<AbsenceTypeSelectorResponse[]>([]);

    const [viewError, setViewError] = useState<any>();
    const postValidation = useRef<IPostValidation | null>(null);

    const inputRef = useRef<HTMLInputElement>(undefined);

    ////////////////////////////////////////////////////////////////////////////

    ///
    const validate = (dto: UserAbsenceResponse): IPostValidation => {
        return {
            absenceTypeId: dto.absenceTypeId == null
                ? t("shared:Validation.RequiredField")
                : undefined,
            validFrom: dto.validFrom == null
                ? t("shared:Validation.RequiredField")
                : !moment(dto.validFrom).isValid()
                    ? t("shared:Validation.InvalidDate")
                    : (dto.validUntil != null && dto.validFrom > dto.validUntil)
                        ? t("shared:Validation.InvalidDateRangeStart")
                        : undefined,
            validUntil: dto.validUntil != null && !moment(dto.validUntil).isValid()
                ? t("shared:Validation.InvalidDate")
                : dto.validUntil != null && dto.validUntil <= dto.validFrom
                    ? t("shared:Validation.InvalidDateRangeEnd")
                    : undefined,
            delegateUserId: dto.delegateUser.length == 0
                ? t("shared:Validation.RequiredField")
                : undefined,
        } as IPostValidation;
    };

    ///
    const handleUpdateRecord = (name: keyof UserAbsenceResponse, v: any) => {
        setViewData({ ...viewData, [name]: v });
    };

    ///
    const handleDeactivate = () => {
        setDialog(<MprcConfirmDialog
            open
            message={t("User.UserAbsenceAssistantDialog.ConfirmDeactivateMessage")}
            onClose={
                (result) => {
                    setDialog(null);
                    if (result) {
                        setDeactivating(true);
                    }
                }}
        />);
    };

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT's
    ////////////////////////////////////////////////////////////////////////////

    // Ao iniciar pela primeira vez...
    useEffect(() => {
        async function getData() {
            try {
                if (absenceTypes.current.length == 0) {
                    const resultAbsenceTypes = await api.v1.selectors.absenceTypes({ appliesToUserStatus: null });
                    absenceTypes.current = resultAbsenceTypes.data;
                }

                var result = await api.v1.user.getAbsence({ userId });
                setViewData(result.data);
            }
            catch (e) {
                setViewError(e);
            }
            setLoading(false);
        }

        if (loading) getData();
        else setTimeout(() => inputRef.current?.focus(), 100);

    }, [loading]);

    // Ao salvar
    useEffect(() => {
        async function postData() {
            postValidation.current = validate(viewData);

            const canSave = Object.values(postValidation.current).filter((x) => x != undefined).length == 0;

            if (canSave) {

                try {
                    if (viewData.userAbsenceId == null) {
                        await api.v1.userManagement.addAbsence(buildPostDto(userId, viewData));
                    }
                    else {
                        await api.v1.userManagement.updateAbsence({
                            userId,
                            userAbsenceId: viewData.userAbsenceId,
                            validUntil: viewData.validUntil
                        });
                    }
                    enqueueSnackbar(t("User.UserAbsenceAssistantDialog.SuccessMessage"), { variant: "success" });
                    onClose(true);
                }
                catch (e) {
                    const vErrors = e as any;
                    postValidation.current = {
                        absenceTypeId: vErrors["absenceTypeId"],
                        validFrom: vErrors["validFrom"],
                        validUntil: vErrors["validUntil"],
                        delegateUserId: vErrors["delegateUserId"],
                    };
                }
            }

            setSaving(false);
        }

        if (saving) postData();

    }, [saving]);

    // Ao desativar
    useEffect(() => {
        async function postData() {
            try {
                await api.v1.userManagement.desactivateAbsence({
                    userId,
                    userAbsenceId: viewData.userAbsenceId!,
                });
                enqueueSnackbar(t("User.UserAbsenceAssistantDialog.DeactivateSuccessMessage"), { variant: "success" });
                onClose(true);
            }
            catch (e) {
                setViewError(e);
            }
            setDeactivating(false);
        }

        if (deactivating) postData();

    }, [deactivating]);


    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////
    const disabled = saving || loading || deactivating;

    return (
        <div>
            {dialog}

            <Dialog open fullWidth maxWidth="sm" onClose={() => onClose(false)}>
                <MprcDialogTitleClose
                    title={t("User.UserAbsenceAssistantDialog.Title")}
                    subtitle={name}
                    onClose={() => onClose(false)}
                    shrinkPadding
                />
                <DialogContent>
                    <MprcRequestErrorReport error={viewError} />
                    <MprcRequestIndicator type={loading ? "request" : "post"} visible={loading || saving || deactivating} />

                    <Grid container spacing={2} direction="column" width="100%">
                        <Grid size={{ xs: 12 }}>
                            <FormControl
                                fullWidth
                                error={postValidation.current?.absenceTypeId != undefined}
                            >
                                <InputLabel required >{t("User.UserAbsenceAssistantDialog.AbsenceTypeLabel")}</InputLabel>
                                <Select
                                    value={viewData.absenceTypeId}
                                    label={t("User.UserAbsenceAssistantDialog.AbsenceTypeLabel")}
                                    disabled={viewData.userAbsenceId != null}
                                    onChange={(e) => handleUpdateRecord("absenceTypeId", e.target.value)}
                                    error={postValidation.current?.absenceTypeId != undefined}
                                >
                                    {absenceTypes.current?.map((x) => (
                                        <MenuItem key={x.absenceTypeId} value={x.absenceTypeId}>
                                            {x.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormHelperText error={postValidation.current?.absenceTypeId != undefined}>
                                <MprcStringToHtml text={postValidation.current?.absenceTypeId} color="error" />
                            </FormHelperText>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <Grid container spacing={4}>
                                <Grid>
                                    <MprcDatePicker
                                        label={t("User.UserAbsenceAssistantDialog.ValidFromLabel")}
                                        error={postValidation.current?.validFrom != undefined}
                                        helperText={<MprcStringToHtml text={postValidation.current?.validFrom} color="error" />}
                                        maxDate={viewData.validUntil}
                                        required
                                        value={viewData.validFrom}
                                        disabled={viewData.userAbsenceId != null}
                                        onChange={(date) => handleUpdateRecord("validFrom", date)}
                                    />
                                </Grid>
                                <Grid>
                                    <MprcDatePicker
                                        label={t("User.UserAbsenceAssistantDialog.ValidUntilLabel")}
                                        minDate={viewData.validFrom}
                                        error={postValidation.current?.validUntil != undefined}
                                        helperText={<MprcStringToHtml text={postValidation.current?.validUntil} color="error" />}
                                        value={viewData.validUntil ?? null}
                                        onChange={(date) => handleUpdateRecord("validUntil", date)}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <UserAutoComplete
                                label={t("User.UserAbsenceAssistantDialog.DelegateUserLabel")}
                                multiSelect={false}
                                selectedItems={viewData.delegateUser}
                                disabled={viewData.userAbsenceId != null}
                                onError={(e) => setViewError(e)}
                                onChange={(items) => handleUpdateRecord("delegateUser", items)}
                                error={postValidation.current?.delegateUserId != undefined}
                                helperText={
                                    postValidation.current?.delegateUserId
                                        ? <MprcStringToHtml text={postValidation.current?.delegateUserId ?? ""} color="error" />
                                        : undefined
                                }
                            />
                        </Grid>
                    </Grid>

                    <Box mt={2}>
                        <FormHelperText>{t("User.UserAbsenceAssistantDialog.DisclaimerText")}</FormHelperText>
                    </Box>

                </DialogContent>
                <DialogActions>
                    <Grid size="grow" mt={2}>
                        <Grid container spacing={4}>
                            <Grid size="grow">
                                {viewData.userAbsenceId &&
                                    <MprcButton
                                        title={t("User.UserAbsenceAssistantDialog.DeleteButton")}
                                        text={t("User.UserAbsenceAssistantDialog.DeleteButton")}
                                        color="error"
                                        variant="contained"
                                        onClick={handleDeactivate}
                                    />
                                }
                            </Grid>
                            <Grid>
                                <Stack direction="row" spacing={1}>
                                    <ButtonCancel
                                        onClick={() => onClose(false)}
                                    />
                                    <ButtonSave
                                        variant="contained"
                                        disabled={disabled}
                                        onClick={() => setSaving(true)}
                                    />
                                </Stack>
                            </Grid>
                        </Grid>
                    </Grid>
                </DialogActions>
            </Dialog>
        </div>
    );
};
